/// <amd-module name="Wit/Common/WorkItemControls/WorkItemFieldControl" />
import { useObservable, useObservableArray } from "VSS/Core/Observable";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext, usePageContext } from "VSS/Platform/Layout";
import { DatePicker } from "VSSUI/DatePicker";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { IdentityPickerDropdown } from "VSSUI/IdentityPicker";
import { IListBoxItem, wrapListBoxItems } from "VSSUI/ListBox";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { getDateInClientTimeZone, getDateInUserTimeZone } from "Wit/Common/DateUtils";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { getNumberInUserFormat } from "Wit/Common/Utilities/FormatUtils";
import { getAllowedIdentityTypes } from "Wit/Common/Utilities/Identity";
import { WITMeMacroPeoplePickerProvider } from "Wit/Common/WITMeMacroPeoplePickerProvider";
import { IterationPathMacros } from "Wit/Common/WiqlOperators";
import { AreaPathDropdown } from "Wit/Common/WorkItemControls/AreaPathDropdown";
import { IterationDropdown } from "Wit/Common/WorkItemControls/IterationDropdown";
import { getWorkItemFieldService, IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { getWorkItemFieldService as getWorkItemFieldService2, IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import {
    convertIIdentityToWorkItemIdentityRef,
    convertIdentityRefToIIdentity,
    convertWorkItemIdentityRefFromFieldValue,
    convertWorkItemIdentityRefToIIdentity,
    isIdentityRef,
    isWorkItemIdentityRef
} from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { IField } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as React from "react";

export type FieldValue = WorkItemIdentityRef | string | Date | number | boolean | undefined | null;

export interface IWorkItemControlProps {
    allowFreeform?: boolean;
    allowedValues?: string[];
    ariaLabel?: string;
    autoSelect?: boolean;
    className?: string;
    disabled?: boolean;
    /** prefer to specify field over fieldEntry but fieldEntry works almost as well when you don't have an item to get an IField from */
    fieldEntry?: IFieldEntry;
    /** prefer to specify field over fieldEntry */
    field?: IField;
    fieldName?: string;
    /** include supported macros if field is iteration path */
    includeIterationMacros?: boolean;
    inputType?: string;
    initialValue?: FieldValue;
    onChange?: (value: FieldValue, changed: boolean) => void;

    /** if the control is a DatePicker, expand it as soon as it's mounted */
    datePickerExpandOnMount?: boolean;
    /** called when the DatePicker collapses */
    datePickerOnCollapse?: () => void;
    /** uses picker provider with MacroMe if the field is Identity */
    useMacroMePickerProvider?: boolean;
    /** forbid service principles from appearing in the dropdown */
    excludeServicePrincipals?: boolean;
}

/**
 * Show an editable control based on the field that is passed in
 */
export const WorkItemFieldControl: React.FunctionComponent<IWorkItemControlProps> = props => {
    const { ariaLabel, className, disabled, fieldName, includeIterationMacros, initialValue, onChange } = props;
    const pageContext = usePageContext();
    const useNewWorkItemFieldService = isFeatureFlagEnabled(pageContext, FeatureFlags.UseNewWorkItemFieldService, false);
    const fieldService = useNewWorkItemFieldService ? getWorkItemFieldService2(pageContext) : getWorkItemFieldService(pageContext);
    const [loadingField, setLoadingField] = React.useState(false);
    const [fieldEntry, setFieldEntry] = React.useState<IFieldEntry | undefined>(() => {
        let initialFieldEntry = props.fieldEntry ?? props.field?.fieldDefinition;
        if (!initialFieldEntry && fieldName) {
            // try to get the field synchronously before making an async call
            initialFieldEntry = fieldService.getFieldByReferenceName(fieldName);
        }

        return initialFieldEntry;
    });

    React.useEffect(() => {
        if (!fieldEntry && fieldName) {
            const loadField = async () => {
                setLoadingField(true);
                if (useNewWorkItemFieldService) {
                    await (fieldService as IWorkItemFieldService2).loadFields();
                } else {
                    await (fieldService as IWorkItemFieldService).getFieldsAsync();
                }
                const field = fieldService.getFieldByReferenceName(fieldName);
                setFieldEntry(field);
                setLoadingField(false);
            };

            loadField();
        }
    });

    React.useEffect(() => {
        if (props.fieldEntry) {
            setFieldEntry(props.fieldEntry);
        }
    }, [props.fieldEntry]);

    if (loadingField) {
        return <Spinner />;
    }

    // try to make sure fieldEntry is always specified for the children (and ourself)
    const childProps = { ...props, fieldEntry };
    if (fieldEntry?.isIdentity) {
        return <WitIdentityPickerDropdown {...childProps} />;
    } else if (fieldEntry?.type === FieldType.DateTime) {
        return <WitDatePicker {...childProps} />;
    } else if (fieldEntry?.referenceName === CoreFieldRefNames.AreaPath) {
        return (
            <AreaPathDropdown
                ariaLabel={ariaLabel}
                className={className}
                disabled={disabled}
                onSetPath={path => {
                    onChange && onChange(path, path !== initialValue);
                }}
                selectedText={initialValue?.toString()}
            />
        );
    } else if (fieldEntry?.referenceName === CoreFieldRefNames.IterationPath) {
        return (
            <IterationDropdown
                ariaLabel={ariaLabel}
                className={className}
                disabled={disabled}
                macros={includeIterationMacros ? IterationPathMacros : undefined}
                onSetPath={path => {
                    onChange && onChange(path, path !== initialValue);
                }}
                selectedText={initialValue?.toString()}
            />
        );
    } else {
        return <WitTextField {...childProps} />;
    }
};

const WitIdentityPickerDropdown: React.FunctionComponent<IWorkItemControlProps> = ({
    ariaLabel,
    className,
    disabled,
    field,
    initialValue,
    onChange,
    useMacroMePickerProvider,
    excludeServicePrincipals,
    fieldEntry
}) => {
    const excludeGroups = field?.filterByScope?.excludeGroups;

    const { pageContext } = useComponentContext();
    const initialIdentity = getIIdentityFromFieldValue(pageContext, initialValue);
    const [observableIdentity] = useObservable(initialIdentity);
    const [allowedValues, setAllowedValues] = React.useState<string[] | undefined>(undefined);

    React.useEffect(() => {
        const getAllowedValues = async () => {
            const useNewWorkItemFieldService = isFeatureFlagEnabled(pageContext, FeatureFlags.UseNewWorkItemFieldService, false);
            const fieldService = useNewWorkItemFieldService ? getWorkItemFieldService2(pageContext) : getWorkItemFieldService(pageContext);
            let values: string[] | undefined;
            if (field) {
                values = await fieldService.getAllowedValues(
                    field.fieldDefinition.referenceName,
                    field.workItem.project.guid,
                    field.fieldDefinition.workItemType.name
                );
            } else if (fieldEntry) {
                values = await fieldService.getAllowedValues(fieldEntry.referenceName);
            }
            setAllowedValues(values);
        };
        getAllowedValues();
    }, [setAllowedValues]);

    const pickerProvider = React.useMemo(() => {
        const CurrentPickerProvider = useMacroMePickerProvider ? WITMeMacroPeoplePickerProvider : PeoplePickerProvider;
        const provider = new CurrentPickerProvider(pageContext, {
            identityTypes: getAllowedIdentityTypes(pageContext, {
                excludeServicePrincipals,
                excludeGroups
            })
        });
        if (provider.setAdditionalEntries && allowedValues) {
            provider.setAdditionalEntries(allowedValues);
        }
        return provider;
    }, [pageContext, excludeServicePrincipals, excludeGroups, useMacroMePickerProvider, allowedValues]);

    return (
        <IdentityPickerDropdown
            ariaLabel={ariaLabel}
            className={className}
            disabled={disabled}
            onChange={identity => {
                observableIdentity.value = identity;
                if (onChange) {
                    const witIdentityRef = identity && convertIIdentityToWorkItemIdentityRef(pageContext, identity);
                    const initialWitIdentityRef = initialIdentity && convertIIdentityToWorkItemIdentityRef(pageContext, initialIdentity);
                    onChange(witIdentityRef, witIdentityRef?.distinctDisplayName !== initialWitIdentityRef?.distinctDisplayName);
                }
            }}
            pickerProvider={pickerProvider}
            value={observableIdentity}
        />
    );
};

function getIIdentityFromFieldValue(pageContext: IVssPageContext, value: FieldValue) {
    if (!value) {
        return undefined;
    }

    if (typeof value === "string") {
        value = convertWorkItemIdentityRefFromFieldValue(pageContext, value);
    }

    if (isWorkItemIdentityRef(value)) {
        return convertWorkItemIdentityRefToIIdentity(pageContext, value);
    } else if (isIdentityRef(value)) {
        return convertIdentityRefToIIdentity(pageContext, value);
    } else {
        return undefined;
    }
}

const WitDatePicker: React.FunctionComponent<IWorkItemControlProps> = ({
    ariaLabel,
    className,
    datePickerExpandOnMount,
    datePickerOnCollapse,
    disabled,
    initialValue,
    onChange
}) => {
    const initialDate = initialValue instanceof Date ? getDateInUserTimeZone(initialValue) : undefined;
    const [dateValue] = useObservable(initialDate);
    return (
        <DatePicker
            ariaLabel={ariaLabel}
            className={className}
            disabled={disabled}
            expandOnMount={datePickerExpandOnMount}
            showClear={true}
            value={dateValue}
            onChange={date => {
                const preparedForSavingDate = date ? getDateInClientTimeZone(date) : undefined;
                dateValue.value = preparedForSavingDate;
                onChange && onChange(preparedForSavingDate, preparedForSavingDate?.getTime() !== initialDate?.getTime());
            }}
            onCollapse={datePickerOnCollapse}
        />
    );
};

const WitTextField: React.FunctionComponent<IWorkItemControlProps> = ({
    allowFreeform,
    allowedValues,
    ariaLabel,
    autoSelect,
    className,
    disabled,
    fieldEntry,
    inputType,
    initialValue,
    onChange
}) => {
    const [allowedValuesList] = useObservableArray<IListBoxItem>(wrapListBoxItems(allowedValues ?? []) ?? []);
    const initialValueString = initialValue?.toString();

    const initialValueStringToUse =
        fieldEntry?.type === FieldType.Double
            ? getNumberInUserFormat(initialValue as number)?.toString()
            : initialValueString;

    const [value] = useObservable(initialValueStringToUse);
    const dropdown = new DropdownSelection();
    React.useEffect(() => {
        allowedValuesList.value = wrapListBoxItems(allowedValues ?? []) ?? [];
        if (allowedValues) {
            const index = allowedValues.findIndex(v => v === initialValueString);
            if (index >= 0 && dropdown.selectable(index)) {
                dropdown.select(index);
            }
        }
    });

    if (allowedValues && allowedValues.length > 0) {
        return (
            <EditableDropdown
                allowFreeform={allowFreeform ?? true}
                allowTextSelection
                ariaLabel={ariaLabel}
                autoSelect={autoSelect ?? false}
                className={className}
                disabled={disabled}
                items={allowedValuesList}
                onValueChange={item => {
                    if (!item) {
                        dropdown.clear();
                    }
                    const newValue = item?.text;

                    value.value = newValue;
                    onChange && onChange(newValue, newValue !== initialValueString);
                }}
                selectedText={initialValueString}
                selection={dropdown}
                minCalloutWidth={180}
            />
        );
    } else {
        const textFieldInputTypeCondition =
            fieldEntry?.type === FieldType.Integer || fieldEntry?.type === FieldType.Double;
        const getInputType = (inputType: string | undefined): "password" | "text" | "button" | "number" => {
            switch (inputType) {
                case "text":
                case "number":
                case "password":
                case "button":
                    return inputType;
                default:
                    return textFieldInputTypeCondition ? "number" : "text";
            }
        };

        const textFieldInputType = getInputType(inputType);
        return (
            <TextField
                ariaLabel={ariaLabel}
                autoSelect={autoSelect ?? false}
                containerClassName={className}
                className={className}
                disabled={disabled}
                inputType={textFieldInputType}
                value={value}
                onChange={(e, newValue) => {
                    value.value = newValue;
                    onChange && onChange(newValue, newValue !== initialValueString);
                }}
            />
        );
    }
};
